import React, { useEffect, useRef, useState } from 'react';

type Star = {
    x: number;
    y: number;
    speed: number;
    radius: number;
    color: string;
};

type Props = {
    children: React.ReactNode;
    /** Star colors. Pass theme-aware colors. Default: white + brand pink/purple. */
    colors?: string[];
    /** Number of stars. Default 35. */
    starCount?: number;
    /** Border radius to match the wrapped element (px or any CSS length). Default 4. */
    borderRadius?: number | string;
    /** If the wrapped element has a visible border, inset the canvas by this many px so stars don't cover it. */
    borderWidth?: number;
};

/**
 * Wraps a clickable element (button, link) and overlays a hyperspace-style
 * particle effect that appears on hover, pauses on click, and respects light/dark theming.
 */
const HyperspaceOverlay: React.FC<Props> = ({
    children,
    colors,
    starCount = 35,
    borderRadius = 4,
    borderWidth = 0,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const rafRef = useRef<number | null>(null);
    const dimsRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);

    const palette = colors ?? ['#FFFFFF', '#868e96', '#343a40'];
    const paletteRef = useRef(palette);
    paletteRef.current = palette;

    const initStars = (w: number, h: number) => {
        const p = paletteRef.current;
        starsRef.current = Array.from({ length: starCount }, () => ({
            x: Math.random() * w,
            y: Math.random() * h,
            speed: 0.4 + Math.random() * 2.8,
            radius: 0.5 + Math.random() * 1.6,
            color: p[Math.floor(Math.random() * p.length)],
        }));
    };

    // Re-color existing stars when the palette changes (e.g. light/dark toggle),
    // preserving their positions so the field doesn't teleport on theme switch.
    useEffect(() => {
        for (const star of starsRef.current) {
            star.color = palette[Math.floor(Math.random() * palette.length)];
        }
        // Join colors for a stable dep — parents often pass a fresh array each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(colors ?? []).join(',')]);

    // Size the canvas to match the wrapper, re-init stars on resize
    useEffect(() => {
        const wrapper = wrapperRef.current;
        const canvas = canvasRef.current;
        if (!wrapper || !canvas) return;

        const handleResize = () => {
            const rect = wrapper.getBoundingClientRect();
            const w = Math.max(0, rect.width - borderWidth * 2);
            const h = Math.max(0, rect.height - borderWidth * 2);
            if (w === 0 || h === 0) return;
            canvas.width = w;
            canvas.height = h;
            dimsRef.current = { w, h };
            initStars(w, h);
        };

        handleResize();
        const ro = new ResizeObserver(handleResize);
        ro.observe(wrapper);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Animation loop — runs while hovered, pauses on press
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const draw = () => {
            const { w, h } = dimsRef.current;
            ctx.clearRect(0, 0, w, h);
            for (const star of starsRef.current) {
                ctx.beginPath();
                ctx.fillStyle = star.color;
                ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        const tick = () => {
            if (!hovered) return;
            const { w, h } = dimsRef.current;
            if (!pressed) {
                for (const star of starsRef.current) {
                    star.x -= star.speed;
                    if (star.x < -2) {
                        star.x = w + 2;
                        star.y = Math.random() * h;
                    }
                }
            }
            draw();
            rafRef.current = requestAnimationFrame(tick);
        };

        if (hovered) {
            rafRef.current = requestAnimationFrame(tick);
        }

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [hovered, pressed]);

    return (
        <div
            ref={wrapperRef}
            style={{
                position: 'relative',
                width: '100%',
                borderRadius,
                overflow: 'hidden',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => {
                setHovered(false);
                setPressed(false);
            }}
            onMouseDown={() => setPressed(true)}
            onMouseUp={() => setPressed(false)}
        >
            {children}
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    inset: borderWidth,
                    width: `calc(100% - ${borderWidth * 2}px)`,
                    height: `calc(100% - ${borderWidth * 2}px)`,
                    borderRadius:
                        typeof borderRadius === 'number'
                            ? Math.max(0, borderRadius - borderWidth)
                            : borderRadius,
                    pointerEvents: 'none',
                    transition: 'opacity 250ms ease',
                    opacity: hovered ? 1 : 0,
                }}
            />
        </div>
    );
};

export default HyperspaceOverlay;
